import React, { KeyboardEventHandler, useState, forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSearch,
  faCalendar,
  faEye,
  faEyeSlash,
  faClipboard,
} from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import { FieldError, FieldValues, UseFormRegister } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

type Props = {
  placeholder?: string | undefined | null;
  defaultValue?: string | number | string[] | undefined;
  required?: boolean;
  customClass?: string;
  type?:
    | "text"
    | "password"
    | "search"
    | "email"
    | "date"
    | "datetime-local"
    | "comment"
    | "number";
  autoComplete?: string;
  error?: FieldError;
  onSubmit?: () => void;
  onKeyDown?: KeyboardEventHandler;
  canCopyToClipboard?: boolean;
};

export const WTextInput = forwardRef<
  HTMLInputElement,
  Props & ReturnType<UseFormRegister<FieldValues>>
>((props, ref) => {
  const {
    onBlur,
    onChange,
    name,
    placeholder = "",
    defaultValue,
    required,
    disabled,
    type = "text",
    autoComplete,
    error,
    customClass,
    onKeyDown,
  } = props;

  const inputType = type === "search" ? "text" : type;
  const [showPassword, setShowPassword] = useState(false);
  const { t: tCommon } = useTranslation("common");

  const onCopyToClipboard = (textToCopy: string) => {
    toast?.success(tCommon("copied-in-clipboard")?.toString());
    navigator.clipboard.writeText(textToCopy);
  };

  const [inputValue, setInputValue] = useState(defaultValue || "");

  return (
    <div className="ek-input group">
      <input
        type={showPassword ? "text" : inputType}
        placeholder=" "
        autoComplete={autoComplete}
        className={clsx([
          inputType === "number" && "hide-arrows",
          error && error.type && "error-input",
          customClass,
        ])}
        name={name}
        onKeyDown={onKeyDown}
        ref={ref}
        defaultValue={defaultValue || inputValue}
        disabled={disabled}
        onBlur={onBlur}
        onChange={(e) => {
          onChange(e);
          setInputValue(e.target.value);
        }}
      />
      {props?.canCopyToClipboard && (
        <div
          onClick={() => onCopyToClipboard(defaultValue?.toString() ?? "")}
          className="absolute hidden cursor-pointer right-5 top-5 group-hover:block"
        >
          <FontAwesomeIcon icon={faClipboard} className="text-ekgreen" />
        </div>
      )}
      <div className="ek-placeholder">
        {placeholder}
        {required && <span className="text-red-500">*</span>}
      </div>
      {error && error.type && (
        <div className="input-error-text text-red-500!">{error.message}</div>
      )}
      {type === "password" && (
        <span
          onClick={() => setShowPassword(!showPassword)}
          className="cursor-pointer ek-input-icon"
        >
          <FontAwesomeIcon
            icon={showPassword ? faEye : faEyeSlash}
            className="w-5 h-5"
          />
        </span>
      )}
      {type === "search" && (
        <button className="ek-input-icon ek-search-icon">
          <FontAwesomeIcon icon={faSearch} />
        </button>
      )}
      {(type === "date" || type === "datetime-local") && (
        <button className="bg-white ek-input-icon ek-search-icon">
          <FontAwesomeIcon icon={faCalendar} />
        </button>
      )}
      {type === "comment" && (
        <button
          onClick={props.onSubmit}
          className="ek-input-icon ek-search-icon"
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      )}
    </div>
  );
});

WTextInput.displayName = "TextInput";

export default WTextInput;
